import React, { useEffect, useState } from 'react';
import {
  Grid,
  Card,
  CardHeader,
  Stack,
  Typography,
  Button,
  alpha,
  CardContent,
  CardActions,
} from '@mui/material';
import { getEngagementReportsData } from '../../services/reports.service';
import { getDate, formatDate } from '../../utils/datetime.util';
import Datepicker from '../form-elements/datepicker.component';
import Iconify from '../common/iconify.component';
import EngagementPlatformTable from './engagement-platform-table.component';
import {
  LISTING_ACTIVE_USERS_COLUMNS,
  LISTING_APP_SESSIONS_COLUMNS,
  LISTING_CONTENT_PLAYS_COLUMNS,
} from '../../config/module-configs/engagement-reports.config';

const EngagementReports = () => {
  const [filters, setFilters] = useState({
    startDate: getDate(-7),
    endDate: getDate(0),
    reloadCounter: 0,
    loading: true,
    error: false,
  });
  const [data, setData] = useState({
    activeUsers: [],
    videoPlays: [],
    appSessions: [],
  });

  const handleStartDateChange = (v) => {
    setFilters((prev) => ({
      ...prev,
      startDate: v,
    }));
  };
  const handleEndDateChange = (v) => {
    setFilters((prev) => ({
      ...prev,
      endDate: v,
    }));
  };
  const handleRefreshData = () => {
    setFilters((prev) => ({
      ...prev,
      reloadCounter: prev.reloadCounter + 1,
    }));
  };

  useEffect(() => {
    setFilters((prev) => ({ ...prev, loading: true, error: false }));

    const startDate = formatDate(filters.startDate, 'YYYY-MM-DD');
    const endDate = formatDate(filters.endDate, 'YYYY-MM-DD');

    getEngagementReportsData(startDate, endDate)
      .then((res) => {
        setData({
          activeUsers: res?.data?.activeUsers || [],
          videoPlays: res?.data?.videoPlays || [],
          appSessions: res?.data?.appSessions || [],
        });
        setFilters((prev) => ({ ...prev, loading: false }));
      })
      .catch(() => {
        setFilters((prev) => ({
          ...prev,
          loading: false,
          error: true,
        }));
      });
  }, [filters.reloadCounter, filters.startDate, filters.endDate]);

  return (
    <>
      <Card sx={{ p: 1.5, mb: 2 }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          alignItems="center"
          justifyContent="flex-end"
        >
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            alignItems="center"
            useFlexGap
            flexWrap="wrap"
          >
            <Datepicker
              id="eng_start_date"
              name="eng_start_date"
              label="Start Date"
              defaultValue={filters.startDate}
              onChange={handleStartDateChange}
              readOnly={filters.loading}
              sx={{ width: 170 }}
            />
            <Typography variant="body2" sx={{ alignSelf: 'center' }}>
              To
            </Typography>
            <Datepicker
              id="eng_end_date"
              name="eng_end_date"
              label="End Date"
              defaultValue={filters.endDate}
              onChange={handleEndDateChange}
              readOnly={filters.loading}
              sx={{ width: 170 }}
            />
            <Button
              color="inherit"
              startIcon={<Iconify icon="ic:twotone-refresh" />}
              onClick={handleRefreshData}
              disabled={filters.loading}
              sx={{
                fontWeight: 700,
                textTransform: 'none',
                borderRadius: '10px',
                px: 2,
                '&:hover': {
                  bgcolor: (theme) => alpha(theme.palette.grey[500], 0.08),
                  transform: 'translateY(-2px)',
                },
                transition: (theme) =>
                  theme.transitions.create(['transform', 'background-color']),
                height: 48,
              }}
            >
              Refresh
            </Button>
          </Stack>
        </Stack>
      </Card>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%' }}>
            <CardHeader
              title="Active Users"
              subheader="Unique active users by platform"
            />
            <CardContent>
              <EngagementPlatformTable
                columns={LISTING_ACTIVE_USERS_COLUMNS}
                data={data.activeUsers}
                loading={filters.loading}
                error={filters.error}
              />
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%' }}>
            <CardHeader
              title="Content Play Counts"
              subheader="Total video and audio play counts by platform"
            />
            <CardContent>
              <EngagementPlatformTable
                columns={LISTING_CONTENT_PLAYS_COLUMNS}
                data={data.videoPlays}
                loading={filters.loading}
                error={filters.error}
              />
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card>
            <CardHeader
              title="Platform-wise App Sessions"
              subheader="Session count, total duration, and average session duration by platform"
            />
            <CardContent>
              <EngagementPlatformTable
                columns={LISTING_APP_SESSIONS_COLUMNS}
                data={data.appSessions}
                loading={filters.loading}
                error={filters.error}
              />
            </CardContent>
            <CardActions>
              <Typography variant="caption">
                * This report includes only completed app sessions.
              </Typography>
            </CardActions>
          </Card>
        </Grid>
      </Grid>
    </>
  );
};

export default EngagementReports;
